import { useMemo, useState } from 'react'

import { BubbleChatIcon, HelpCircleIcon } from '@hugeicons/react'

import { sanitizeHtml } from '@/shared/lib/html/sanitize'
import { resolveSupportContactHref } from '@/shared/lib/contact/resolveSupportContactHref'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useRecords } from '@/shared/hooks/useRecords'
import { PageHeader, PageHero, Screen } from '@/shared/ui/Layout'
import { Badge } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import { SearchInput } from '@/shared/ui/Form'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

interface Faq {
  id: number | string
  name: string
  description: string
}

interface DigitalProductFAQProps {
  digitalProductId: string
}

const CARD_RADIUS = 'rounded-[var(--radius-card-gamification)]'

export const DigitalProductFAQ = ({ digitalProductId }: DigitalProductFAQProps) => {
  const [search, setSearch] = useState('')
  const { digitalProduct } = useDigitalProductContext()

  const supportHref = resolveSupportContactHref(digitalProduct.contact)

  const { records: faqs, isLoading } = useRecords({
    r: `/user/digital_products/${digitalProductId}/faqs`,
  }) as { records: Faq[]; isLoading: boolean }

  const filtered = useMemo(() => {
    if (!search.trim()) return faqs

    const term = search.toLowerCase()
    return faqs.filter(
      f =>
        f.name.toLowerCase().includes(term) ||
        f.description.toLowerCase().includes(term),
    )
  }, [faqs, search])

  const isEmpty = !isLoading && filtered.length === 0

  return (
    <Screen>
      <Screen.Context className="mb-4">
        <PageHeader title="Central de Ajuda" />

        <PageHero art="/faq/hero-icon.png" artFit="contain" variant="light">
          <PageHero.Title>Como podemos te ajudar?</PageHero.Title>
          <PageHero.Subtitle>
            Encontre respostas rápidas para suas dúvidas.
          </PageHero.Subtitle>
          {!isLoading && faqs.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              <Badge variant="available" tone="light" showIcon={false}>
                {faqs.length} artigos
              </Badge>
            </div>
          )}
        </PageHero>
      </Screen.Context>

      <Screen.Sticky className="py-2">
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar dúvida..."
        />
      </Screen.Sticky>

      <Screen.Content className="flex flex-col gap-3 pb-6 pt-1">
        {isLoading &&
          Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className={`animate-pulse ${CARD_RADIUS} bg-white p-4 shadow-sm`}
            >
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 shrink-0 rounded-xl bg-gray-200" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-3/4 rounded bg-gray-200" />
                  <div className="h-2.5 w-1/2 rounded bg-gray-200" />
                </div>
                <div className="h-4 w-4 shrink-0 rounded bg-gray-200" />
              </div>
            </div>
          ))}

        {isEmpty && (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-brand-light)]">
              <HelpCircleIcon size={28} className="text-[var(--color-brand)]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Nenhuma pergunta encontrada
              </p>
              <p className="mt-0.5 text-2xs text-gray-600">
                {search.trim()
                  ? 'Tente buscar outro termo.'
                  : 'Nenhuma dúvida disponível no momento.'}
              </p>
            </div>
          </div>
        )}

        {!isLoading && filtered.length > 0 && (
          <Accordion type="single" collapsible>
            {filtered.map(faq => (
              <AccordionItem
                key={faq.id}
                value={String(faq.id)}
                className={`mb-3 overflow-hidden ${CARD_RADIUS} border-0 bg-white shadow-sm last:mb-0`}
              >
                <AccordionTrigger className="flex items-center gap-3 px-4 py-4 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--color-brand-light)]">
                    <HelpCircleIcon size={15} className="text-[var(--color-brand)]" />
                  </div>
                  <span className="flex-1 text-left text-sm font-semibold text-gray-900 leading-snug">
                    {faq.name}
                  </span>
                </AccordionTrigger>

                <AccordionContent className="px-4 pb-4 pt-0">
                  <div className="border-t border-gray-200 pt-3">
                    <div
                      className="text-sm leading-relaxed text-gray-700 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-lg"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(faq.description) }}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}

        {!isLoading && supportHref && (
          <div
            className={`flex items-center gap-3 ${CARD_RADIUS} bg-[var(--color-brand-light)] px-4 py-4 shadow-sm`}
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm">
              <BubbleChatIcon
                size={24}
                className="text-[var(--color-brand)]"
                variant="solid"
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="m-0 text-sm font-bold leading-tight text-gray-900">
                Ainda não encontrou sua resposta?
              </p>
              <p className="m-0 mt-0.5 text-2xs text-gray-600">
                Fale com nossa equipe de suporte.
              </p>
            </div>
            <Button
              href={supportHref}
              target="_blank"
              rel="noopener noreferrer"
              variant="primary"
              size="sm"
              showTrailingIcon={false}
              className="shrink-0"
            >
              Falar com suporte
            </Button>
          </div>
        )}
      </Screen.Content>
    </Screen>
  )
}
